import React, { useId } from 'react'

/**
 * WaveSliders — three labelled coloured slider rows used by `prep-wave` and `prep-clarity`.
 *
 * Each slider is an accessible range input (0–10) with a label and colour accent.
 * The `fields` prop drives the labels, accent colours, and optional sub-label on select.
 *
 * Used by:
 *  - WaveCard (prep-wave): work / energy / outside — moss / olive / clay
 *  - ClarityCard (prep-clarity): priorities / role / strategy — with auto-unfurl note under lowest
 *
 * Phase E: new interaction (no prior equivalent; replaces ClarityRingsScale for clarity).
 */

const DEFAULT_FIELDS = [
  { key: 'work',    label: 'Work',    color: 'var(--moss-500)',    bg: 'var(--moss-100)' },
  { key: 'energy',  label: 'Energy',  color: 'var(--olive-500)',   bg: 'var(--olive-200)' },
  { key: 'outside', label: 'Outside', color: 'var(--clay-600)',    bg: 'var(--clay-100)' },
]

function SliderRow({ field, value, onChange, disabled, showUnfurl, unfurlPlaceholder }) {
  const id = useId()
  const filled = value != null

  return (
    <div style={styles.row}>
      <label htmlFor={id} style={{ ...styles.label, color: field.color }}>
        {field.label}
      </label>
      <div style={styles.sliderWrap}>
        <input
          id={id}
          type="range"
          min={0}
          max={10}
          step={1}
          value={value ?? 5}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={`${field.label} — ${value ?? 'unset'} out of 10`}
          style={{
            ...styles.slider,
            accentColor: field.color,
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <span style={{ ...styles.value, color: filled ? field.color : 'var(--color-text-faint)' }}>
          {filled ? value : '—'}
        </span>
      </div>
      {showUnfurl && (
        <textarea
          rows={1}
          placeholder={unfurlPlaceholder ?? `What's unclear about ${field.label.toLowerCase()}?`}
          disabled={disabled}
          style={styles.unfurl}
          aria-label={`Note on ${field.label}`}
          onChange={() => {/* Phase E — note wiring deferred to PrepCard data layer */}}
        />
      )}
    </div>
  )
}

export function WaveSliders({ fields = DEFAULT_FIELDS, values = {}, onChange, disabled = false, unfurlLowest = false, unfurlPlaceholder }) {
  // Find the lowest-scored field (for ClarityCard's auto-unfurl)
  const lowestKey = unfurlLowest
    ? fields.reduce((low, f) => {
        const v = values[f.key] ?? 10
        return v < (values[low?.key] ?? 10) ? f : low
      }, null)?.key
    : null

  function handleChange(key, val) {
    onChange({ ...values, [key]: val })
  }

  return (
    <div style={styles.wrap}>
      {fields.map((field) => (
        <SliderRow
          key={field.key}
          field={field}
          value={values[field.key] ?? null}
          onChange={(val) => handleChange(field.key, val)}
          disabled={disabled}
          showUnfurl={unfurlLowest && field.key === lowestKey && (values[field.key] ?? 10) < 5}
          unfurlPlaceholder={unfurlPlaceholder}
        />
      ))}
    </div>
  )
}

WaveSliders.DEFAULT_FIELDS = DEFAULT_FIELDS

const styles = {
  wrap: { padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 },
  row: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-mono)' },
  sliderWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  slider: { flex: 1, cursor: 'pointer', height: 4 },
  value: { fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, minWidth: 16, textAlign: 'right' },
  unfurl: {
    marginTop: 4, width: '100%', border: 'none', outline: 'none', background: 'transparent',
    borderBottom: '1px solid var(--color-line)', fontSize: 12, color: 'var(--color-text-primary)',
    resize: 'none', overflow: 'hidden', fontFamily: 'inherit', padding: '2px 0',
  },
}
